import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';

interface CartItem {
  id: number;
  produto_id: number;
  quantidade: number;
  preco_unitario: number;
  observacoes?: string;
  personalizacoes: any[];
  produto_nome: string;
  imagem_url?: string;
  descricao?: string;
}

interface Cart {
  id: number;
  status: string;
  observacoes?: string;
  data_criacao: string;
  data_expiracao: string;
}

interface EnderecoEntrega {
  rua: string;
  bairro: string;
  numero_casa: string;
  cep: string;
  cidade?: string;
}

interface CartData {
  carrinho: Cart | null;
  itens: CartItem[];
  total: number;
  endereco_entrega: EnderecoEntrega | null;
  entregaGratis: boolean;
}

export const useCart = (empresaId: number) => {
  const { usuario, token } = useAuth();
  const [cartData, setCartData] = useState<CartData>({
    carrinho: null,
    itens: [],
    total: 0,
    endereco_entrega: null,
    entregaGratis: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Função para verificar entrega grátis
  const verificarEntregaGratis = useCallback(async () => {
    if (!usuario || !token) {
      console.log('🚫 verificarEntregaGratis: Usuário ou token não disponível');
      return;
    }
    
    console.log('🔍 verificarEntregaGratis: Verificando entrega grátis para usuário:', usuario.id);
    console.log('🔍 verificarEntregaGratis: URL da API:', `/api/carrinho/entrega-gratis/${usuario.id}`);
    
    try {
      const response = await api.get(`/api/carrinho/entrega-gratis/${usuario.id}`);
      console.log('✅ verificarEntregaGratis: Resposta completa da API:', response);
      console.log('✅ verificarEntregaGratis: Dados da resposta:', response.data);
      
      const temEntregaGratis = response.data.temDireitoEntregaGratis;
      
      console.log('🎁 verificarEntregaGratis: Valor extraído temEntregaGratis:', temEntregaGratis);
      console.log('🎁 verificarEntregaGratis: Tipo do valor:', typeof temEntregaGratis);
      
      setCartData(prev => {
        const newData = {
          ...prev,
          entregaGratis: temEntregaGratis
        };
        console.log('🎁 verificarEntregaGratis: Estado anterior:', prev.entregaGratis);
        console.log('🎁 verificarEntregaGratis: Novo estado:', newData.entregaGratis);
        return newData;
      });
      
      console.log('🎁 verificarEntregaGratis: Entrega grátis definida como:', temEntregaGratis);
    } catch (error: any) {
      console.error('❌ Erro ao verificar entrega grátis:', error);
      console.error('❌ Detalhes do erro:', error.response?.data);
      setCartData(prev => ({
        ...prev,
        entregaGratis: false
      }));
    }
  }, [usuario, token]);

  // Função para formatar endereço
  const formatarEndereco = useCallback((endereco: EnderecoEntrega) => {
    if (!endereco) return '';
    const cidade = endereco.cidade ? `, ${endereco.cidade}` : '';
    return `${endereco.rua}, ${endereco.numero_casa} - ${endereco.bairro}${cidade}, CEP: ${endereco.cep}`;
  }, []);

  // Carregar carrinho do servidor
  const loadCart = useCallback(async () => {
    if (!usuario || !token) {
      // Não fazer nada se não há usuário logado
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.get(`/api/carrinho/${empresaId}`);
      setCartData(response.data);
    } catch (error: any) {
      if (error.response?.status === 404) {
        setCartData({
          carrinho: null,
          itens: [],
          total: 0,
          endereco_entrega: null,
          entregaGratis: false
        });
      } else {
        console.error('Erro ao carregar carrinho:', error);
        // Não mostrar toast de erro se não há usuário logado
        if (usuario && token) {
          toast.error('Erro ao carregar carrinho', { duration: 2000 });
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [usuario, token, empresaId]);

  // Adicionar item ao carrinho (otimizado)
  const addToCart = useCallback(async (
    produtoId: number, 
    quantidade: number = 1, 
    observacoes?: string, 
    personalizacoes?: any[]
  ) => {
    if (!usuario || !token) {
      // Para usuários não logados, mostrar modal de login ao invés de erro
      return false;
    }

    if (isUpdating) {
      return false; // Evitar múltiplas adições simultâneas
    }

    setIsUpdating(true);
    try {
      const payload = {
        produto_id: produtoId,
        quantidade,
        observacoes,
        personalizacoes
      };
      
      await api.post(`/api/carrinho/${empresaId}/adicionar`, payload);
      toast.success('Item adicionado ao carrinho!', { duration: 1500 });
      
      // Recarregar carrinho com debounce
      setTimeout(() => loadCart(), 100);
      
      return true;
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.response?.data?.erro || 'Erro ao adicionar item';
      toast.error(errorMessage, { duration: 2000 });
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, [usuario, token, empresaId, loadCart, isUpdating]);

  // Atualizar quantidade de um item
  const updateItemQuantity = useCallback(async (itemId: number, quantidade: number) => {
    if (!usuario || !token) {
      console.log('Usuário não logado - não é possível atualizar quantidade');
      return false;
    }

    setIsUpdating(true);
    try {
      await api.put(`/api/carrinho/${empresaId}/item/${itemId}`, { quantidade });
      await loadCart(); // Recarregar carrinho
      return true;
    } catch (error: any) {
      console.error('Erro ao atualizar quantidade:', error);
      toast.error('Erro ao atualizar quantidade', { duration: 2000 });
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, [usuario, token, empresaId, loadCart]);

  // Remover item do carrinho
  const removeItem = useCallback(async (itemId: number) => {
    if (!usuario || !token) {
      console.log('Usuário não logado - não é possível remover item');
      return false;
    }

    setIsUpdating(true);
    try {
      await api.delete(`/api/carrinho/${empresaId}/item/${itemId}`);
      toast.success('Item removido do carrinho', { duration: 1500 });
      await loadCart(); // Recarregar carrinho
      return true;
    } catch (error: any) {
      console.error('Erro ao remover item:', error);
      toast.error('Erro ao remover item', { duration: 2000 });
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, [usuario, token, empresaId, loadCart]);

  // Limpar carrinho
  const clearCart = useCallback(async () => {
    if (!usuario || !token) {
      console.log('Usuário não logado - não é possível limpar carrinho');
      return false;
    }

    setIsUpdating(true);
    try {
      await api.delete(`/api/carrinho/${empresaId}/limpar`);
      toast.success('Carrinho limpo com sucesso', { duration: 1500 });
      
      // Limpar estado local imediatamente
      setCartData({
        carrinho: null,
        itens: [],
        total: 0,
        endereco_entrega: null,
        entregaGratis: false
      });
      
      // Recarregar carrinho para confirmar
      await loadCart();
      return true;
    } catch (error: any) {
      toast.error('Erro ao limpar carrinho', { duration: 2000 });
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, [usuario, token, empresaId, loadCart]);

  // Atualizar item completo (quantidade, observações, personalizações)
  const updateItem = useCallback(async (
    itemId: number,
    quantidade: number,
    observacoes: string,
    personalizacoes: any[]
  ) => {
    if (!usuario || !token) {
      console.log('Usuário não logado - não é possível atualizar item');
      return false;
    }

    setIsUpdating(true);
    try {
      const payload = {
        quantidade,
        observacoes,
        personalizacoes
      };

      await api.put(`/api/carrinho/${empresaId}/item/${itemId}`, payload);
      await loadCart(); // Recarregar carrinho
      return true;
    } catch (error: any) {
      console.error('Erro ao atualizar item:', error);
      toast.error('Erro ao atualizar item', { duration: 2000 });
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, [usuario, token, empresaId, loadCart]);

  // Atualizar observações do carrinho
  const updateCartObservations = useCallback(async (observacoes: string) => {
    if (!usuario || !token) {
      console.log('Usuário não logado - não é possível atualizar observações');
      return false;
    }

    setIsUpdating(true);
    try {
      await api.put(`/api/carrinho/${empresaId}/observacoes`, { observacoes });
      toast.success('Observações atualizadas com sucesso', { duration: 1500 });
      await loadCart(); // Recarregar carrinho
      return true;
    } catch (error: any) {
      console.error('Erro ao atualizar observações:', error);
      toast.error('Erro ao atualizar observações', { duration: 2000 });
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, [usuario, token, empresaId, loadCart]);

  // Verificar se o carrinho está expirado
  const isCartExpired = useCallback(() => {
    if (!cartData.carrinho) return false;
    return new Date(cartData.carrinho.data_expiracao) < new Date();
  }, [cartData.carrinho]);

  // Calcular tempo restante para expiração
  const getTimeUntilExpiration = useCallback(() => {
    if (!cartData.carrinho) return null;
    
    const now = new Date();
    const expiration = new Date(cartData.carrinho.data_expiracao);
    const diff = expiration.getTime() - now.getTime();
    
    if (diff <= 0) return null;
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return { hours, minutes };
  }, [cartData.carrinho]);

  // Carregar carrinho quando usuário ou empresa mudar
  useEffect(() => {
    console.log('🔄 useEffect useCart: Usuário mudou:', usuario?.id, 'Token:', !!token);
    
    if (usuario && token) {
      console.log('✅ useEffect useCart: Carregando carrinho e verificando entrega grátis');
      loadCart();
      // Aguardar um pouco para garantir que o carrinho foi carregado
      setTimeout(() => {
        verificarEntregaGratis();
      }, 500);
    } else {
      console.log('🚫 useEffect useCart: Limpando carrinho - usuário não logado');
      // Limpar dados do carrinho quando não há usuário logado
      setCartData({
        carrinho: null,
        itens: [],
        total: 0,
        endereco_entrega: null,
        entregaGratis: false
      });
    }
  }, [usuario?.id, token, empresaId]);

  // Verificar expiração a cada 60 segundos (reduzido de 30s)
  useEffect(() => {
    if (!cartData.carrinho) return;

    const interval = setInterval(() => {
      if (isCartExpired()) {
        toast.error('Seu carrinho expirou e foi limpo automaticamente', { duration: 2000 });
        clearCart();
      }
    }, 60000); // Verificar a cada 60 segundos

    return () => clearInterval(interval);
  }, [cartData.carrinho, isCartExpired, clearCart]);

  return {
    // Estado
    cartData,
    isLoading,
    isUpdating,
    
    // Funções
    loadCart,
    addToCart,
    updateItemQuantity,
    removeItem,
    clearCart,
    updateCartObservations,
    updateItem,
    verificarEntregaGratis,
    
    // Utilitários
    formatarEndereco,
    isCartExpired,
    getTimeUntilExpiration,
    
    // Computed values
    itemCount: cartData.itens.reduce((sum, item) => sum + item.quantidade, 0),
    hasItems: cartData.itens.length > 0
  };
};
